import { type Announcement } from "@core/shared/types";
import {
  getAnnouncementStyles,
  getBorderColor,
  getIconStyles,
} from "@core/shared/utils/color-mapper";
import { URLS } from "@core/shared/utils";
import { cn } from "@design-system/utils";
import { MegaphoneIcon, PinIcon } from "lucide-react";
import { LessonCard } from "@app/grades/components/LessonCard";
import { AssessmentCard } from "@app/assessments/components/AssessmentCard";
import { BaseLink } from "@app/design-system/components/link";

type AnnouncementPanelProps = {
  announcement: Announcement;
  className?: string;
};

export function AnnouncementPanel({
  announcement,
  className,
}: AnnouncementPanelProps) {
  const { title, description, color, colorType, isPinned, lesson, assessment } = announcement;

  if (!title && !description) return null;

  const hasRelatedContent = lesson || assessment;

  return (
    <div className="space-y-6">
      {/* Main Announcement Panel */}
      <div
        className={cn(
          "overflow-hidden rounded-lg shadow-sm transition-all hover:shadow-md",
          getAnnouncementStyles(color, colorType),
          hasRelatedContent && "sm:pb-8",
          className,
        )}
      >
        {title ? (
          <>
            {/* Header with title */}
            <div
              className={cn(
                "flex items-center justify-between border-b p-4",
                getBorderColor(color, colorType),
              )}
            >
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-full",
                    getIconStyles(color, colorType),
                    // Add dark mode support for icon container
                    "dark:bg-gray-700 dark:border-gray-600",
                    // Ensure icon is visible on dark backgrounds
                    colorType === "solid" &&
                      color !== "white" &&
                      color !== "yellow"
                      ? "text-white"
                      : "text-current",
                  )}
                >
                  <MegaphoneIcon className="h-5 w-5 text-gray-600 dark:text-gray-200" />
                </div>
                <div>
                  <h3 className="font-semibold">{title}</h3>
                </div>
              </div>
              {isPinned && <PinIcon className="h-4 w-4 opacity-70" />}
            </div>

            {/* Content */}
            {description && (
              <div className="p-4">
                <p className="whitespace-pre-line text-sm leading-relaxed">
                  {description}
                </p>
              </div>
            )}
          </>
        ) : (
          // Description-only UI
          <div className="relative">
            {isPinned && (
              <PinIcon className="absolute right-4 top-4 h-4 w-4 opacity-70" />
            )}
            <div className="flex flex-col items-center justify-center p-8">
              <div className="relative w-full max-w-2xl">
                <div className="absolute -left-3 -top-3 text-4xl opacity-20">
                  &quot;
                </div>
                <p
                  className={cn(
                    "whitespace-pre-line text-center text-xl font-medium leading-relaxed",
                  )}
                >
                  {description}
                </p>
                <div className="absolute -bottom-3 -right-3 text-4xl opacity-20">
                  &quot;
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Floating Related Content - Positioned to emerge from announcement bottom */}
      {hasRelatedContent && (
        <div className="relative !-mt-10 mb-8">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            {/* Lesson */}
            {lesson && (
              <div 
                key={lesson.id} 
                className="transform transition-all duration-300 hover:scale-105 hover:-translate-y-1"
                style={{ 
                  filter: 'drop-shadow(0 12px 35px rgba(0, 0, 0, 0.2))',
                  zIndex: 10
                }}
              >
                {/* Mobile: Simplified lesson card */}
                <div className="sm:hidden">
                  <BaseLink to={URLS.lessons.view(lesson)}>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 border border-gray-200 dark:border-gray-600 transition-all hover:shadow-xl hover:scale-105 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h1m4 0h1m-6-8h8a2 2 0 012 2v8a2 2 0 01-2 2H8a2 2 0 01-2-2v-8a2 2 0 012-2z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {lesson.name}
                          </h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {lesson.instructor?.name}
                          </p>
                        </div>
                      </div>
                    </div>
                  </BaseLink>
                </div>
                {/* Desktop: Full lesson card */}
                <div className="hidden sm:block w-96">
                  <LessonCard lesson={lesson} />
                </div>
              </div>
            )}

            {/* Assessment */}
            {assessment && (
              <div 
                key={assessment.id} 
                className="transform transition-all duration-300 hover:scale-105 hover:-translate-y-1"
                style={{ 
                  filter: 'drop-shadow(0 12px 35px rgba(0, 0, 0, 0.2))',
                  zIndex: 10
                }}
              >
                {/* Mobile: Simplified assessment card */}
                <div className="sm:hidden">
                  <BaseLink to={URLS.assessments.view(assessment)}>
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-3 border border-gray-200 dark:border-gray-600 transition-all hover:shadow-xl hover:scale-105 cursor-pointer">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-success-100 dark:bg-success-900 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-success-600 dark:text-success-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {assessment.title}
                          </h4>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {assessment.totalQuestions} {assessment.totalQuestions === 1 ? 'question' : 'questions'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </BaseLink>
                </div>
                {/* Desktop: Full assessment card */}
                <div className="hidden sm:block w-80">
                  <AssessmentCard assessment={assessment} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}